import { useEffect, useState } from 'react';
import NicknameBar from '../components/NicknameBar';

interface PollOption {
  id: string;
  text: string;
  votes: number;
  percentage: number;
}

interface PollResults {
  pollId: string;
  options: PollOption[];
  totalVotes: number;
  winningOptionId: string | null;
  isActive: boolean;
}

interface PollResultsScreenProps {
  pollId: string;
}

function PollResultsScreen({ pollId }: PollResultsScreenProps) {
  const [results, setResults] = useState<PollResults | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await fetch(`/api/polls/${pollId}/results`);
        if (response.ok) {
          const data = await response.json();
          setResults(data);
        }
      } catch (error) {
        console.error('[PollResultsScreen] Error fetching results:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
    // Refresh ogni 2 secondi
    const interval = setInterval(fetchResults, 2000);
    return () => clearInterval(interval);
  }, [pollId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-parchment-400"></div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <p className="text-xl text-team-red">Risultati non disponibili</p>
      </div>
    );
  }

  // Ordina per percentuale decrescente
  const sortedOptions = [...results.options].sort((a, b) => b.percentage - a.percentage);

  return (
    <div className="flex flex-col min-h-screen p-4 pt-10">
      <NicknameBar />
      {/* Titolo */}
      <div className="text-center py-6">
        <h1 className="text-4xl font-heading font-bold text-ink">Risultati</h1>
      </div>

      {/* Risultati */}
      <div className="flex-1 flex flex-col justify-center space-y-4 max-w-md mx-auto w-full">
        {sortedOptions.map((option) => {
          const isWinner = option.id === results.winningOptionId;

          return (
            <div
              key={option.id}
              className={`rounded-xl p-4 border-2 transition-all ${
                isWinner
                  ? 'bg-team-yellow-bg border-team-yellow'
                  : 'parchment-card border-parchment-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {isWinner && <span className="text-2xl">🏆</span>}
                  <p className={`text-lg font-bold ${isWinner ? 'text-team-yellow' : 'text-ink'}`}>
                    {option.text}
                  </p>
                </div>
                <p className={`text-2xl font-bold ${isWinner ? 'text-team-yellow' : 'text-ink'}`}>
                  {option.percentage}%
                </p>
              </div>

              {/* Progress bar */}
              <div className="w-full h-3 bg-parchment-300 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-1000 ${
                    isWinner ? 'bg-team-yellow' : 'bg-parchment-400'
                  }`}
                  style={{ width: `${option.percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PollResultsScreen;
